import {useMemo, useState} from "react";

import {
    ConversionContextProvider,
    type PartialLearningTimeInfo,
} from "@khan/analytics-learning-time";
import {GuideStateApi} from "@khan/guide-state";
import type {Conversation} from "@khan/guide-types";

import {useGuideStateChangeEffect} from "../hooks/use-guide-state-change-effect.ts";

interface Props {
    children: React.ReactNode;
}

/**
 * GuideConversionContextProvider populates learning time data with AI Guide
 * related information for marked conversions.
 */
export const GuideConversionContextProvider = (
    props: Props,
): React.ReactElement => {
    const [activeThreadId, setActiveThreadId] = useState(
        getIsActiveThread(GuideStateApi.latest().conversation)
            ? GuideStateApi.latest().threadId
            : "",
    );

    useGuideStateChangeEffect("threadId", ({conversation, threadId}) => {
        setActiveThreadId(getIsActiveThread(conversation) ? threadId : "");
    });

    useGuideStateChangeEffect("conversation", ({conversation, threadId}) => {
        setActiveThreadId(getIsActiveThread(conversation) ? threadId : "");
    });

    const learningTimeInfo: PartialLearningTimeInfo = useMemo(
        () => ({
            content: {
                aiGuideThreadId: activeThreadId ?? "",
            },
        }),
        [activeThreadId],
    );

    return (
        <ConversionContextProvider learningTimeInfo={learningTimeInfo}>
            {props.children}
        </ConversionContextProvider>
    );
};

const getIsActiveThread = (conversation: Conversation) => {
    // We only want to include the threadID in conversions if the user has
    // actually interacted with Khanmigo.
    return conversation.some((message) => message.type === "outgoing");
};
